import { Injectable } from '@angular/core';
import {
  LoginCognitoCallback,
  CognitoUtil,
  LoggedInCallback,
} from './utils/cognito.service';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {
  UPDATE_USER_BRAND,
  LAST_ACTIVITY_TIME,
} from './utils/state-management/actions';
import { NgRedux, select } from '@angular-redux/store';
import { IAppState } from './utils/state-management/store';
import { LOGOUT } from './utils/state-management/actions';
import { UserManagement } from './utils/state-management/action-classes';
import { Utility } from './utils/utility';
import { LoaderService } from './loader.service';
import { Constants } from './utils/Constants';
import apiConfig from './api-client/api-config';
import { CommonService } from './common.service';
import { ApiRequestService } from './api-client/api-requests.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { DrAuthService } from './dr-auth';

@Injectable()
export class UserLoginService {
  @select() user: any;
  private date: any;

  private onLoginSuccess = (
    callback: LoginCognitoCallback,
    session: CognitoUserSession
  ) => {
    callback.cognitoCallback(null, session);
  };

  private onLoginError = (callback: LoginCognitoCallback, err: any) => {
    callback.cognitoCallback(err.message, null);
    this.loaderService.isLoading.next(false);
  };

  constructor(
    public cognitoUtil: CognitoUtil,
    private ngRedux: NgRedux<IAppState>,
    private userManagement: UserManagement,
    private utility: Utility,
    public loaderService: LoaderService,
    private commonService: CommonService,
    private apiRequest: ApiRequestService,
    private router : Router,
    private drAuthService: DrAuthService,
  ) {}

  authenticate(body : any) {
    this.date = Date.now();
    this.loaderService.isLoading.next(true);
    return this.apiRequest.callApi(Constants.API_METHODS.POST, apiConfig.login.uri, body, true)
      
  }

  resendCode(body: any) {
    return this.apiRequest.callApi(
      Constants.API_METHODS.POST,
      apiConfig.reSendMFA.uri,
      body,
      true
    );
  }

  verifyCode(body: any) {
    return this.apiRequest.callApi(
      Constants.API_METHODS.POST,
      apiConfig.verifyMFA.uri,
      body,
      false
    );
  }

  forgotPassword(username: string, callback: LoginCognitoCallback) {
    let userData = {
      Username: username,
      Pool: this.cognitoUtil.getUserPool(),
    };

    let cognitoUser = new CognitoUser(userData);

    cognitoUser.forgotPassword({
      onSuccess: function () {},
      onFailure: function (err) {
        callback.cognitoCallback(err.message, null);
      },
      inputVerificationCode() {
        callback.cognitoCallback(null, null);
      },
    });
  }

  confirmNewPassword(
    email: string,
    verificationCode: string,
    password: string,
    callback: LoginCognitoCallback
  ) {
    let userData = {
      Username: email,
      Pool: this.cognitoUtil.getUserPool(),
    };

    let cognitoUser = new CognitoUser(userData);

    cognitoUser.confirmPassword(verificationCode, password, {
      onSuccess: function () {
        callback.cognitoCallback(null, null);
      },
      onFailure: function (err) {
        callback.cognitoCallback(err.message, null);
      },
    });
  }



  logout(): any {
    const email = this.drAuthService.getEmail();
    if (!email) return;

    const primaryKey = this.drAuthService.getPrimaryKey(email);
    const secondaryKey = this.drAuthService.getSecondaryKey(email);

    const primaryToken = this.drAuthService.getToken(primaryKey, 'refreshToken');
    const secondaryToken = this.drAuthService.getToken(secondaryKey, 'refreshToken');

    if (!primaryToken && !secondaryToken) return;

    const body = {
      refreshtoken: primaryToken,
      refreshtokensecondary: secondaryToken,
    };



    this.apiRequest.callApi(Constants.API_METHODS.POST, apiConfig.logout.uri, body, true)
      .subscribe({
        next: (res) => {
          this.commonService.removeTokens();
          this.whiteListLocalStorage();
          this.ngRedux.dispatch({ type: LOGOUT });
          window.location.href = `${window.location.origin}/home/login`;
        }
      });
  }

  globalLogout(callback: any) {
    this.date = Date.now();
    this.loaderService.isLoading.next(true);

    try {
      let cognitoUser = this.cognitoUtil.getCurrentUser();
      cognitoUser.getSession((err: any, session: any) => {
        if (session.isValid()) {
          cognitoUser.globalSignOut(callback);
        }
        this.ngRedux.dispatch({ type: LOGOUT });
        let brand = localStorage.getItem('userBrand');
        let configObject = JSON.parse(localStorage.getItem('config'));
        localStorage.clear();
        localStorage.setItem('config', JSON.stringify(configObject));

        this.ngRedux.dispatch({
          type: UPDATE_USER_BRAND,
          brand: brand,
        });
      });
    } catch (err) {
      this.utility.removeLoader(this.date);
      this.loaderService.isLoading.next(false);
    }
    this.utility.removeLoader(this.date);
    this.loaderService.isLoading.next(false);
  }

  isAuthenticated(callback: LoggedInCallback) {
    if (callback == null)
      throw 'UserLoginService: Callback in isAuthenticated() cannot be null';
    if (this.cognitoUtil.getCurrentUser()) {
      this.cognitoUtil.getCurrentUser().getSession((err: any, session: any) => {
        if (session && session.isValid()) {
          callback.isLoggedIn(err, true);
        } else {
          console.log('Here False');
          callback.isLoggedIn(err, false);
        }
      });
    } else {
      callback.isLoggedIn(null, false);
    }
  }

  canActivate(callback: any) {
    if (this.cognitoUtil.getCurrentUser()) {
      this.cognitoUtil.getCurrentUser().getSession((err: any, session: any) => {
        if (localStorage.getItem('isSignUp')) {
          return callback(true);
        } else {
          if (
            session.isValid() &&
            this.ngRedux.getState().userObject.phoneActive
          ) {
            return callback(true);
          } else {
            return callback(false);
          }
        }
      });
    } else {
      return callback(false);
    }
  }

  getLastActivityTime() {
    return this.ngRedux.getState().time;
  }

  UpadteLastActivity() {
    this.ngRedux.dispatch({ type: LAST_ACTIVITY_TIME, time: Date.now() });
  }

  whiteListLocalStorage() {
    const perserveMap = new Map();
    const whiteList = ['theme', 'userBrand', 'cookiesEventTrigger', 'config'];

    for(const item of whiteList){
      perserveMap.set(item, localStorage.getItem(item));
    }

    localStorage.clear();
    for(const [key,value] of perserveMap){
      localStorage.setItem(key, value);
    }
  }
}
